/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { UsergroupAddOutlined } from '@ant-design/icons'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { FamilyScope } from '@sportninja/common/services/player/familyScope'
import Button from 'src/components/ts-components/Button'
import { paragraphSmall } from 'src/components/css'
import DetailTableSection from '../../player/playerDetails/components/DetailTableSection'
import { familyColumns } from '../constants/familyColumns'
import { useFamilyActions } from '../useFamilyActions'
import { useFamilyMembers } from '../../hooks/useFamilyMembers'
import type { FamilyRowAction } from '../utils/familyRowActions'
import { toFamilyTableRow } from '../utils/familyTableRow'
import type { FamilyTableRow } from '../utils/familyTableRow'
import type { FamilyContactPlayerSource } from './toFamilyContactPlayer'
import FamilyGuideLink from './FamilyGuideLink'
import FamilyMembersSkeleton from './FamilyMembersSkeleton'
import FamilyRowActions from './FamilyRowActions'

interface FamilyMembersSectionProps {
  playerId: string
  /** Which backend routes to use. Omitted → self-scoped ('me'). */
  scope?: FamilyScope
  /** Player for the contact-modal card, when the caller already has it. */
  player?: FamilyContactPlayerSource | null
}

const FAMILY_COLUMNS = familyColumns<FamilyTableRow>()

const LOAD_ERROR_MESSAGE = "Couldn't load family members."

/**
 * The Family Members section of the Edit Player modal: the family list with a
 * per-row ⋯ menu (resend / edit / delete) and an Invite Family Member action.
 */
const FamilyMembersSection = ({
  playerId,
  scope,
  player,
}: FamilyMembersSectionProps) => {
  const { members, isFirstLoad, error, refetch } = useFamilyMembers(
    playerId,
    scope
  )
  const { openInvite, handleEdit, handleDelete, handleResend, modals } =
    useFamilyActions(playerId, scope, player)

  // The list is cached (5 min stale time); the modal edits family data
  // elsewhere, so fetch fresh each time the section mounts.
  useEffect(() => {
    refetch()
  }, [refetch])

  const columns: ColDef<FamilyTableRow>[] = [
    ...FAMILY_COLUMNS,
    {
      colId: 'actions',
      pinned: 'right',
      width: 64,
      sortable: false,
      resizable: false,
      cellRenderer: ({ data }: ICellRendererParams<FamilyTableRow>) =>
        data ? (
          <FamilyRowActions
            member={data.member}
            onSelect={(action: FamilyRowAction) => {
              if (action === 'resend') {
                handleResend(data.member)
              } else if (action === 'edit') {
                handleEdit(data.member)
              } else {
                handleDelete(data.member)
              }
            }}
          />
        ) : null,
    },
  ]

  const inviteAction = (
    <Button
      size='compact'
      variant='outlined'
      text={capitalizeWords('Invite Family Member')}
      icon={<UsergroupAddOutlined />}
      onClick={openInvite}
      style={inviteButtonStyle}
    />
  )

  // Undefined leaves the grid in place; the skeleton / error replace it.
  let body: ReactNode
  if (isFirstLoad) {
    body = <FamilyMembersSkeleton />
  } else if (error && members.length === 0) {
    body = <span css={errorTextStyle}>{LOAD_ERROR_MESSAGE}</span>
  }

  return (
    <>
      <DetailTableSection<FamilyTableRow>
        sectionKey='family'
        rows={members.map(toFamilyTableRow)}
        columnDefs={columns}
        loading={isFirstLoad}
        titleHint={<FamilyGuideLink />}
        actions={inviteAction}
      >
        {body}
      </DetailTableSection>
      {modals}
    </>
  )
}

export default FamilyMembersSection

// ---- styled ----
/* Sits beside the 40px Columns button, so it matches that height. */
const inviteButtonStyle = css`
  height: 40px;
`

const errorTextStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_100};
`
